import { useEffect } from 'react';

import { loadStripe } from '@stripe/stripe-js/pure';
import { useAtom } from 'jotai';

import { stripeAtom } from './stripe.atoms';

/**
 * Get or initialize the Stripe instance. This ensures loadStripe is only called once
 * across the entire application, even if used in multiple components.
 *
 * @param publishableKey - The Stripe publishable key. If not provided, uses VITE_STRIPE_PUBLISHABLE_KEY from env.
 * @param enabled - Whether to load the Stripe instance. If not provided, uses true.
 * @returns Promise that resolves to the Stripe instance or null
 */
export const useLoadStripe = ({
  publishableKey,
  enabled = true,
}: {
  publishableKey?: string;
  enabled?: boolean;
}) => {
  const [stripe, setStripe] = useAtom(stripeAtom);

  useEffect(() => {
    const getStripe = async () => {
      if (!publishableKey) return;
      const stripePromise = await loadStripe(publishableKey);
      setStripe(stripePromise);
    };
    if (publishableKey && !stripe && !!enabled) {
      void getStripe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishableKey, setStripe, enabled]);

  return stripe;
};
